import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import decorators from 'utils/decorators';
import checkoutApi from 'services/api/checkout';
import OrderUtils from 'utils/Order';
import localeUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import Actions from 'actions/Actions';
import store from 'store/Store';
import resourceWrapper from 'utils/framework/resourceWrapper';
import Flush from 'utils/localStorage/Flush';
import locationUtils from 'utils/Location';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import {
    SDD_ERRORS, PAYMENT_ERRORS, BOPIS_PICKUP_ERRORS, SECTION_NAMES
} from 'constants/frictionlessCheckout';
import {
    Divider, Flex, Link, Text
} from 'components/ui';
import ChatEntry from 'components/Content/CustomerService/ChatWithUs/ChatEntry';
import { CHAT_ENTRY } from 'constants/chat';
import orderUtils from 'utils/Order';
import ErrorConstants from 'utils/ErrorConstants';
import cookieUtils from 'utils/Cookies';
import agentAwareUtils from 'utils/AgentAware';
import historyLocationActions from 'actions/framework/HistoryLocationActions';
import { FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';
import Storage from 'utils/localStorage/Storage';
import FrictionlessCheckoutEN from 'utils/locales/FrictionlessCheckout/locales/FrictionlessCheckout_en_US';
import FrictionlessCheckoutFR from 'utils/locales/FrictionlessCheckout/locales/FrictionlessCheckout_fr_CA';

const { ERROR_KEYS, ERROR_CODES } = ErrorConstants;

const getText = localeUtils.getLocalisedDataFromResource(FrictionlessCheckoutEN, FrictionlessCheckoutFR);

const maxAmountToCheckout = Sephora.configurationSettings.afterpayConfigurations?.maxAmountToCheckout || 2000;
const PAYMENT_GROUPS = { CREDIT_CARD: 'CreditCardPaymentGroup' };

const paymentServices = {
    payWithKlarna: {
        paymentService: 'Klarna',
        paymentName: 'payWithKlarna',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.KLARNA,
        limit: 1000
    },
    payWithAfterpay: {
        paymentService: localeUtils.isUS() ? 'Cash App Afterpay' : 'Afterpay',
        paymentName: 'payWithAfterpay',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.AFTERPAY,
        limit: maxAmountToCheckout
    },
    payWithPaze: {
        paymentService: 'Paze',
        paymentName: 'payWithPaze',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.PAZE,
        limit: maxAmountToCheckout
    },
    payWithVenmo: {
        paymentService: 'Venmo',
        paymentName: 'payWithVenmo',
        paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.VENMO,
        limit: maxAmountToCheckout
    }
};

function setShippingMethod({
    orderId, newShippingMethodId, shippingGroupId, successCallback, failureCallback, message, updateWaiveShipping
}) {
    let shipOptionsSendData = {
        orderId,
        shippingGroupId,
        shippingMethodId: newShippingMethodId
    };

    if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
        //If it is Sephora Mirror and extension is installed, waive shipping and handling checkbox is displayed
        shipOptionsSendData = {
            ...shipOptionsSendData,
            waiveShippingFee: message || false
        };
        updateWaiveShipping(message || false);
    }

    return decorators
        .withInterstice(
            checkoutApi.setShippingMethod,
            INTERSTICE_DELAY_MS
        )(shipOptionsSendData)
        .then(() => {
            successCallback();
        })
        .catch(errorData => failureCallback(errorData));
}

function checkPromoEligibility(checkedShippingMethod, orderDetails) {
    const { items = {} } = orderDetails;
    const { promoShippingMethods = [], appliedPromotions = [] } = items;

    const ineligibleShippingPromotions = appliedPromotions
        .filter(
            appliedPromo =>
                appliedPromo.promoShippingMethods &&
                appliedPromo.promoShippingMethods.every(promoMethod => promoMethod !== checkedShippingMethod.shippingMethodType)
        )
        .map(ineligiblePromo => ineligiblePromo.couponCode);

    if (promoShippingMethods.length && ineligibleShippingPromotions.length) {
        return false;
    }

    return true;
}

function getPaymentSectionProps(paymentName, checkoutEnabled) {
    const sectionProps = paymentServices[paymentName] ? { ...paymentServices[paymentName] } : {};
    sectionProps.paymentCheckoutEnabled = checkoutEnabled;
    sectionProps.limit = localeUtils.isFRCanada()
        ? `${sectionProps.limit}${localeUtils.CURRENCY_SYMBOLS.CA_FR}`
        : `${localeUtils.CURRENCY_SYMBOLS.US}${sectionProps.limit}`;

    return sectionProps;
}

function validateCreditCardErrors(payment) {
    if (!payment.isMigrated) {
        return getText('creditCardMigratedError');
    }

    return null;
}

function validatePaymentGroup(paymentGroups, messages, isOrderComplete) {
    const handlerMapper = {
        [PAYMENT_GROUPS.CREDIT_CARD]: validateCreditCardErrors
    };

    const errorMessages = [];

    paymentGroups.forEach(currentPaymentGroup => {
        if (handlerMapper[currentPaymentGroup?.paymentGroupType]) {
            const values = handlerMapper[currentPaymentGroup?.paymentGroupType](currentPaymentGroup?.paymentGroup);

            if (values) {
                errorMessages.push(values);
            }
        }
    });

    if (messages?.type === 'error') {
        errorMessages.push(...messages?.messages);
    }

    if (!messages && !isOrderComplete) {
        errorMessages.push(getText('cardMissingDetailsDefault'));
    }

    return errorMessages.length > 0 ? errorMessages : getText('defaultMessage');
}

function setErrorsFromCheckoutAPI(errorData) {
    const sectionErrors = {};

    if (errorData?.errors?.invalidAddress && errorData?.errorCode === -10180 && SDD_ERRORS.includes(errorData?.key)) {
        sectionErrors[SECTION_NAMES.SDD] = errorData?.errorMessages?.join(' ');
    }

    if (errorData?.scheduledAddrChangeMessage) {
        sectionErrors[SECTION_NAMES.SDD] = errorData?.scheduledAddrChangeMessage?.[0]?.messages?.join(' ');
    }

    if (errorData?.errors?.sameDaySkuOOSException) {
        sectionErrors[SECTION_NAMES.SDD] = errorData?.errorMessages?.join(' ');
    }

    return sectionErrors;
}

function setDeliverToErrors(message, isGiftCardSection = false) {
    const sectionErrors = {};
    sectionErrors[isGiftCardSection ? SECTION_NAMES.GIFT_CARD_DELIVERY : SECTION_NAMES.DELIVER_TO] = message;

    return sectionErrors;
}

function checkDeliverToAndGCErrors(orderDetails, isSDUOnlyOrder, isBopisOrder, sectionToBeConfirmed) {
    const shippingGroups = orderDetails?.shippingGroups?.shippingGroupsEntries || [];

    const sectionErrors = {};

    shippingGroups.forEach(shippingGroup => {
        if ((!shippingGroup?.shippingGroup?.isComplete && !isBopisOrder && !isSDUOnlyOrder) || sectionToBeConfirmed === SECTION_NAMES.DELIVER_TO) {
            const sectionName =
                shippingGroup?.shippingGroupType === orderUtils.SHIPPING_GROUPS.GIFT ? SECTION_NAMES.GIFT_CARD_DELIVERY : SECTION_NAMES.DELIVER_TO;
            sectionErrors[sectionName] = sectionToBeConfirmed === sectionName ? getText('confirmAddress') : getText('defaultMessage');
        }
    });

    return sectionErrors;
}

function checkForCheckoutSectionsCompleteness(orderDetails, isCvvRequired = false, sectionToBeConfirmed = null) {
    let sectionErrors = {};

    const hasSDD = orderDetails?.header?.isSameDayOrder;
    const isSDUOnlyOrder = orderUtils.isSDUOnlyOrder(orderDetails);
    const isPaymentNotComplete = orderDetails?.paymentGroups?.paymentGroupsEntries?.some(
        paymentGroup => paymentGroup?.paymentGroup.isComplete === false
    );
    const isBopisOrder = orderDetails?.header?.isBopisOrder;

    // Ensure if-conditions follow the checkout layout order.
    // Start with "Deliver To" if it's the first section, and so on.
    // This keeps SectionErrors keys aligned with layout sequence,
    // enabling smooth scroll to the first error.

    if (orderDetails.header.isAddressMisMatch) {
        //address mismatch error
        sectionErrors.addressMismatch = getText('defaultMessage');
    }

    const deliverToAndGCErrors = checkDeliverToAndGCErrors(orderDetails, isSDUOnlyOrder, isBopisOrder, sectionToBeConfirmed);

    if (deliverToAndGCErrors) {
        sectionErrors = {
            ...sectionErrors,
            ...deliverToAndGCErrors
        };
    }

    if (hasSDD && !orderDetails?.shippingGroups?.shippingGroupsEntries?.[0]?.shippingGroup?.shippingMethod?.isComplete) {
        // SDD Incomplete error
        sectionErrors[SECTION_NAMES.SDD] = getText('defaultMessage');
    }

    if (
        (!orderDetails?.header?.shippingGroups?.[0]?.shippingMethod?.isComplete && !isBopisOrder && !isSDUOnlyOrder) ||
        sectionToBeConfirmed === SECTION_NAMES.SHIPPING_METHOD
    ) {
        // Shipping method incomplete error
        sectionErrors[SECTION_NAMES.SHIPPING_METHOD] =
            sectionToBeConfirmed === SECTION_NAMES.SHIPPING_METHOD ? getText('confirmAddress') : getText('defaultMessage');
    }

    if (isPaymentNotComplete || orderDetails?.paymentGroups?.paymentMessages?.length > 0) {
        // payment method incomplete error
        sectionErrors[SECTION_NAMES.PAYMENT] = validatePaymentGroup(
            orderDetails?.paymentGroups?.paymentGroupsEntries,
            orderDetails?.paymentGroups?.paymentMessages?.[0],
            orderDetails?.header?.isComplete
        );
    }

    if (isCvvRequired) {
        sectionErrors[SECTION_NAMES.PAYMENT] = getText('confirmCVV');
    }

    if (!orderDetails?.items?.isBIPointsAvailable) {
        // BI Points unavailable error.
        sectionErrors[SECTION_NAMES.BI_BENEFITS] = getText('BIUnavailable');
    }

    if (sectionToBeConfirmed === SECTION_NAMES.GIFT_CARD_DELIVERY) {
        sectionErrors[sectionToBeConfirmed] = getText('confirmAddress');
    }

    return sectionErrors;
}

const ORDER_FRAUD_ERROR_MAP = {
    'checkout.placeOrder.fraud.error.forter_no_fix': {
        errorCode: -8,
        modalTitle: 'error'
    },
    'checkout.placeOrder.fraud.error.client_resolve_coupon': {
        errorCode: -8,
        modalTitle: 'adjustBasket'
    },
    'checkout.placeOrder.fraud.error.client_resolve_sale': {
        errorCode: -8,
        modalTitle: 'adjustBasket'
    },
    'checkout.placeOrder.fraud.error.contact_cs': {
        errorCode: -8,
        modalTitle: 'verificationRequired',
        modalMessage: () => {
            const getFormattedText = resourceWrapper(getText);

            return (
                <Flex
                    flexDirection='column'
                    gap={4}
                >
                    <Text
                        is='p'
                        children={getFormattedText(
                            'contactCS',
                            false,
                            <Link
                                color='blue'
                                underline
                                href='tel:18777374672'
                                children={getText('csPhone')}
                            />
                        )}
                    />
                    <Text is='p'>
                        <Text children={getText('csHours')} />
                        <br />
                        <Text
                            children={getFormattedText(
                                'monFriHours',
                                false,
                                <Text
                                    fontWeight='bold'
                                    children={getText('monToFri')}
                                />
                            )}
                        />
                        <br />
                        <Text
                            children={getFormattedText(
                                'satSunHours',
                                false,
                                <Text
                                    fontWeight='bold'
                                    children={getText('satToSun')}
                                />
                            )}
                        />
                    </Text>
                    <Divider />
                    <ChatEntry type={CHAT_ENTRY.fraudErrCheckout} />
                    <Divider />
                </Flex>
            );
        }
    }
};

function isGuestOrder() {
    return store.getState().order.orderDetails.header.isGuestOrder;
}

const redirectToBasket = () => {
    //remove basket cache so that OOS items show up on basket page with warning message
    Flush.flushBasket();
    const path = '/basket';

    locationUtils.setLocation(path);
};

const redirectOnSpecificErrors = error => {
    if (
        error.key === ERROR_CODES.NOT_ENOUGH_BI_POINTS ||
        error.key === ERROR_CODES.NOT_ENOUGH_POINTS_ERROR ||
        // errorKey for this errors is unknown
        error.errorCode === ERROR_KEYS.SAME_DAY_DISABLED ||
        error.errorCode === ERROR_KEYS.SDU_MISSING_PROFILE_DETAILS
    ) {
        redirectToBasket();
    }
};

function getOrderFraudError(errorData) {
    const defaultErrorMessage = getText('defaultErrorMessage');
    const { errorCode, errorMessages, key: errorKey } = errorData || Empty.Object;

    let isOrderDeclined = false;
    let orderDeclineAction = Empty.Function;

    if (errorKey && Object.hasOwn(ORDER_FRAUD_ERROR_MAP, errorKey) && errorCode === -8) {
        const { modalTitle, modalMessage } = ORDER_FRAUD_ERROR_MAP[errorKey];

        isOrderDeclined = true;

        orderDeclineAction = () => {
            const isGuestCheckout = isGuestOrder();

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText(isGuestCheckout ? 'error' : modalTitle),
                    message: isGuestCheckout
                        ? getText('guestErrorMessage')
                        : modalMessage
                            ? modalMessage()
                            : errorMessages?.length > 0
                                ? errorMessages?.join(' ')
                                : defaultErrorMessage,
                    buttonText: getText('ok'),
                    callback: redirectToBasket,
                    cancelCallback: redirectToBasket
                })
            );
        };
    }

    return {
        isOrderDeclined,
        orderDeclineAction
    };
}

function isSectionLevelError(key) {
    return [...SDD_ERRORS, ...PAYMENT_ERRORS, ...BOPIS_PICKUP_ERRORS].includes(key);
}

function openInfoModal(errorData) {
    const errorMessage = errorData?.errorMessages?.join(' ');
    const htmlMessage = errorMessage.replace(/\n/g, '<br />');
    const forceHtmlMessage = errorMessage !== htmlMessage;
    store.dispatch(
        Actions.showInfoModal({
            isOpen: true,
            isHtml: forceHtmlMessage,
            title: getText('error'),
            message: forceHtmlMessage ? htmlMessage : errorMessage,
            buttonText: getText('ok'),
            cancelCallback: () => redirectOnSpecificErrors(errorData),
            callback: () => redirectOnSpecificErrors(errorData)
        })
    );
}

function getErrorStatesFromSubmitOrderAPI(errorData) {
    const { key, errorMessages } = errorData;
    const { isOrderDeclined, orderDeclineAction } = getOrderFraudError(errorData);
    const defaultErrorMessage = getText('defaultErrorMessage');

    if (errorData) {
        FrictionlessCheckoutBindings.trackPlaceOrderErrorAnalytics(errorData);
    }

    if (isOrderDeclined) {
        orderDeclineAction();

        return null;
    } else if (isSectionLevelError(key)) {
        let sectionName = '';
        const sectionErrors = {};

        // Ensure if-conditions follow the checkout layout order.
        // Start with "Deliver To" if it's the first section, and so on.
        // This keeps SectionErrors keys aligned with layout sequence,
        // enabling smooth scroll to the first error.

        if (BOPIS_PICKUP_ERRORS.includes(key)) {
            sectionName = SECTION_NAMES.BOPIS_PICKUP_INFO;
        }

        if (SDD_ERRORS.includes(key)) {
            sectionName = SECTION_NAMES.SDD;
        }

        if (PAYMENT_ERRORS.includes(key)) {
            sectionName = SECTION_NAMES.PAYMENT;
        }

        sectionErrors[sectionName] = errorMessages?.length > 0 ? errorMessages?.join(' ') : defaultErrorMessage;

        return sectionErrors;
    } else {
        if (!errorData?.errorMessages?.length) {
            //adds default error message when there are no messages from API call.
            errorData.errorMessages = [defaultErrorMessage];
        }

        openInfoModal(errorData);

        return null;
    }
}

function getPaymentMethodName() {
    const orderDetails = store.getState().order.orderDetails;
    const paymentGroupEntry = orderDetails?.paymentGroups?.paymentGroupsEntries?.[0];

    if (paymentGroupEntry?.paymentGroupType === PAYMENT_GROUPS.CREDIT_CARD) {
        return 'standard';
    } else {
        return `continue with ${paymentGroupEntry?.paymentGroup?.paymentDisplayInfo}`;
    }
}

function getPromosData() {
    const promotions = store.getState()?.order?.orderDetails?.promotion || Empty.Object;
    const promoCodes = [],
        promoDisplayName = [],
        promotionIds = [],
        promotionTypes = [],
        sephoraPromotionTypes = [];

    if (Array.isArray(promotions.appliedPromotions)) {
        promotions.appliedPromotions.forEach(
            ({
                couponCode = null, displayName = null, promotionId = null, promotionType = null, sephoraPromotionType = null
            }) => {
                promoCodes.push(couponCode);
                promoDisplayName.push(displayName);
                promotionIds.push(promotionId);
                promotionTypes.push(promotionType);
                sephoraPromotionTypes.push(sephoraPromotionType);
            }
        );
    }

    return {
        promoCodes,
        promoDisplayName,
        promotionIds,
        promotionTypes,
        sephoraPromotionTypes
    };
}

function isFCReleaseThreeEnabled() {
    const isFCReleaseThreeCookieEnabled = cookieUtils.isFCReleaseThreeEnabled();
    const isFCReleaseThreeKSEnabled = Sephora.configurationSettings.frictionlessCheckout?.release3?.isEnabled;

    return isFCReleaseThreeCookieEnabled && isFCReleaseThreeKSEnabled;
}

function isFCReleaseFourEnabled() {
    const isFCReleaseFourCookieEnabled = cookieUtils.isFCReleaseFourEnabled();
    const isFCReleaseFourKSEnabled = Sephora.configurationSettings.frictionlessCheckout?.release4?.isEnabled;

    return isFCReleaseFourCookieEnabled && isFCReleaseFourKSEnabled;
}

function sectionThatNeedsToBeConfirmed(order) {
    if (order.giftCardAddressNeedsToBeConfirmed) {
        return SECTION_NAMES.GIFT_CARD_DELIVERY;
    }

    if (order.addressNeedsToBeConfirmed) {
        return SECTION_NAMES.DELIVER_TO;
    }

    if (order.shippingMethodNeedsToBeConfirmed) {
        return SECTION_NAMES.SHIPPING_METHOD;
    }

    if (order.paymentNeedsToBeConfirmed) {
        return SECTION_NAMES.PAYMENT;
    }

    return null;
}

function isGiftCardAddressComplete(orderDetails) {
    const shipGroup = orderUtils.getPhysicalGiftCardShippingGroup(orderDetails);

    return shipGroup && shipGroup.isComplete;
}

function isCVVMissing(headerPaymentGroups = []) {
    return headerPaymentGroups.some(group => 'securityCodeMissingMsg' in group);
}

function isPaymentInOrderComplete(orderDetails) {
    const orderState = store.getState().order;
    const isZeroDollarOrderWithCVVValidation = orderUtils.isZeroDollarOrderWithCVVValidation();
    const headerPaymentGroups = orderDetails.header.paymentGroups || [];
    const isSDUOnlyOrder = orderUtils.isSDUOnlyOrder(orderDetails);
    const hasAutoReplenishItems = orderUtils.hasAutoReplenishItems(orderDetails);
    const creditCardPaymentGroup = orderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };

    if (isFCReleaseFourEnabled() && (orderState?.pendingCreditCard || isCVVMissing(headerPaymentGroups))) {
        return true; // dont return an error if we have a pending CC to process or if the card has CVV missing.
    }

    if ((isSDUOnlyOrder || hasAutoReplenishItems || isZeroDollarOrderWithCVVValidation) && !creditCardPaymentGroup.isComplete) {
        return false;
    }

    for (let i = 0; i < headerPaymentGroups.length; i++) {
        const group = headerPaymentGroups[i];

        if (!group.isComplete) {
            return false;
        }
    }

    return true;
}

function isPaymentSectionComplete(orderDetails, paymentOptions, shouldCheckForAlternatePayExpanded) {
    // if Klarna, Afterpay, Paze or Venmo are a selected payment method -> always mark
    // Payment Section as incomplete.

    const paymentTypes = ['klarna', 'afterpay', 'paze', 'venmo'];
    const selectedButNotDefault = paymentTypes.some(type => {
        const group = orderUtils.getPaymentGroup(orderDetails, orderUtils.PAYMENT_GROUP_TYPE[type.toUpperCase()]);
        const isDefault = paymentOptions.defaultPayment === type;

        return group && !isDefault;
    });

    // keeps the payment section expanded
    if (shouldCheckForAlternatePayExpanded && selectedButNotDefault) {
        return false;
    }

    return isPaymentInOrderComplete(orderDetails);
}

function isShipAddressComplete(orderDetails) {
    const shipGroup = orderUtils.getHardGoodShippingGroup(orderDetails);

    return shipGroup && shipGroup.isComplete;
}

const normalizeZip = zip => (zip || '').replace(/[^0-9A-Z]/gi, '');

const filterAddressByPreferredZipCode = (preferredZipCode, addressList) => {
    if (!addressList) {
        return null;
    }

    const sliceNumber = localeUtils.isCanada() ? 3 : 5;
    const normPref = normalizeZip(preferredZipCode).slice(0, sliceNumber);

    return addressList.filter(address => {
        const normAddr = normalizeZip(address.postalCode).slice(0, sliceNumber);

        return normPref === normAddr;
    }).length;
};

function isSameDayShipAddressComplete(orderDetails) {
    const shipGroup = orderUtils.getSameDayShippingGroup(orderDetails);

    return shipGroup && shipGroup.isComplete;
}

function isPaymentComplete(checkForNewUser = false, shouldCheckForAlternatePayExpanded = true) {
    const { orderDetails, paymentOptions } = store.getState().order;
    const creditCardPaymentGroup = orderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };
    const isSephoraCardType = orderUtils.isSephoraCardType(creditCardPaymentGroup);
    const isSephoraCardEdit = Storage.session.getItem(LOCAL_STORAGE.EDIT_SEPHORA_CARD);
    const shouldEditSephoraCard = isSephoraCardEdit && !isSephoraCardType;

    const paymentGroupsEntries = orderDetails.paymentGroups && orderDetails.paymentGroups.paymentGroupsEntries;

    if (
        checkForNewUser &&
        paymentGroupsEntries.length === 1 &&
        paymentGroupsEntries[0].paymentGroupType === 'CreditCardPaymentGroup' &&
        paymentGroupsEntries[0].paymentGroup.cardNumber.length === 0
    ) {
        //returning true as we dont want to show error when user doesn even have any payment method saved.
        return true;
    }

    if (shouldEditSephoraCard || !isPaymentSectionComplete(orderDetails, paymentOptions, shouldCheckForAlternatePayExpanded)) {
        return false;
    } else {
        return true;
    }
}

function isDeliverToComplete(addressList = []) {
    const { orderDetails } = store.getState().order;
    const preferredZipCode = store.getState().user.preferredZipCode;
    const hardGoodShipGroup = orderUtils.getHardGoodShippingGroup(orderDetails);
    const sameDayShipGroup = orderUtils.getSameDayShippingGroup(orderDetails);
    const sduShipGroup = orderUtils.getSDUShippingGroup(orderDetails);
    const hasSDUOnlyInSddBasket = orderUtils.hasSDUOnlyInSddBasket(orderDetails);
    const numberOfAddressesByPreferredZipCode = hasSDUOnlyInSddBasket
        ? addressList.length
        : filterAddressByPreferredZipCode(preferredZipCode, addressList);
    const isDefaultAddressSelectedForSDD = sameDayShipGroup?.address?.isDefault;

    if (hardGoodShipGroup && !isShipAddressComplete(orderDetails)) {
        return false;
    } else if (
        sameDayShipGroup &&
        !hasSDUOnlyInSddBasket &&
        (!isSameDayShipAddressComplete(orderDetails) ||
            (addressList?.length &&
                (numberOfAddressesByPreferredZipCode === 0 || (numberOfAddressesByPreferredZipCode > 1 && !isDefaultAddressSelectedForSDD))))
    ) {
        return false;
    } else if (sduShipGroup && !hasSDUOnlyInSddBasket && addressList?.length && numberOfAddressesByPreferredZipCode === 0) {
        return false;
    } else {
        return true;
    }
}

function isGiftCardDeliveryComplete() {
    const { orderDetails } = store.getState().order;
    const physicalGiftCard = orderUtils.getPhysicalGiftCardShippingGroup(orderDetails);
    const sameDayShipGroup = orderUtils.getSameDayShippingGroup(orderDetails);

    //we dont render Gift Card section if we have SDD items in basket
    if (physicalGiftCard && !isGiftCardAddressComplete(orderDetails) && !sameDayShipGroup) {
        return false;
    } else {
        return true;
    }
}

// Map of section names to their completion checkers
const SECTION_COMPLETION_CHECKERS = {
    payment: () => isPaymentComplete(true, false),
    deliverTo: () => isDeliverToComplete(),
    giftCardDeliverTo: () => isGiftCardDeliveryComplete()
};

function updateCheckoutURLBasedOnOrderCompleteness(addressList) {
    let newPath = FC_CHECKOUT_PATH;
    const currentPath = store.getState().historyLocation.path;

    if (!isGiftCardDeliveryComplete()) {
        newPath += `/${FC_CHECKOUT_SECTIONS.GIFT_CARD_DELIVERY.path}`;
    } else if (!isDeliverToComplete(addressList)) {
        newPath += `/${FC_CHECKOUT_SECTIONS.DELIVER_TO.path}`;
    } else if (!isPaymentComplete()) {
        newPath += `/${FC_CHECKOUT_SECTIONS.PAYMENT.path}`;
    }

    if (currentPath === FC_CHECKOUT_PATH) {
        store.dispatch(historyLocationActions.replaceLocation({ path: newPath }));
    } else {
        store.dispatch(historyLocationActions.goTo({ path: newPath }));
    }
}

function formatCanadianPostalCode(postalCode) {
    const normalized = (postalCode || '').replace(/[^0-9A-Z]/gi, '').toUpperCase();

    return normalized.length === 6 ? `${normalized.slice(0, 3)} ${normalized.slice(3)}` : normalized;
}

function setRequestDataForCreditCardUpdate(pendingCreditCard, securityCode) {
    const {
        cardType, lastName, firstName, creditCardId, expirationMonth, expirationYear
    } = pendingCreditCard;
    const requestData = {
        isMarkAsDefault: pendingCreditCard.isDefault,
        creditCard: {
            address: {
                ...pendingCreditCard.address
            },
            creditCardId,
            firstName,
            lastName,
            cardType,
            expirationMonth,
            expirationYear
        }
    };

    if (securityCode) {
        requestData.creditCard.securityCode = securityCode;
    }

    return requestData;
}

export default {
    checkPromoEligibility,
    setShippingMethod,
    getPaymentSectionProps,
    paymentServices,
    checkForCheckoutSectionsCompleteness,
    getErrorStatesFromSubmitOrderAPI,
    SECTION_NAMES,
    getOrderFraudError,
    setErrorsFromCheckoutAPI,
    getPaymentMethodName,
    getPromosData,
    validatePaymentGroup,
    setDeliverToErrors,
    isFCReleaseThreeEnabled,
    isFCReleaseFourEnabled,
    sectionThatNeedsToBeConfirmed,
    isPaymentComplete,
    isDeliverToComplete,
    isGiftCardDeliveryComplete,
    SECTION_COMPLETION_CHECKERS,
    updateCheckoutURLBasedOnOrderCompleteness,
    formatCanadianPostalCode,
    isCVVMissing,
    setRequestDataForCreditCardUpdate
};
